/**
 * One approval request — §7.6.
 *
 * The screen answers three questions in this order, because that is the order a
 * person asks them: *is anything waiting on me*, *what is still blocking this*,
 * and *who has said what*. The middle one is the reason the gate is fetched
 * alongside the request rather than derived here — `GET /api/approvals/documents/:id`
 * serves the history and the gate from one call, and the gate is the same
 * computation publish will refuse on. Deriving "is it blocked" client-side from
 * the reviewer rows would produce a screen that can disagree with the 409.
 *
 * **The gate is about the newest request on the document, not necessarily about
 * this one.** Approval history is unbounded by design — the partial unique index
 * covers the open states only, so a document goes through this repeatedly — and
 * a superseded request must not be painted with the current gate's verdict. That
 * is what `gate.requestId` is compared against below, and getting it wrong would
 * put "cleared to publish" at the top of a request that was rejected last month.
 *
 * Nothing here decides who may act. Removing a reviewer is `document.approve`,
 * adding one is `document.edit`, deciding is the reviewer row itself, and every
 * one of those is resolved in SQL inside the transaction that mutates. The
 * controls are offered and the refusals are rendered.
 */

import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  useAddReviewer,
  useApprovalRequest,
  useDocument,
  useDocumentApprovals,
  useExpireApprovalRequest,
  useMe,
  usePeoplePicker,
  useRemoveReviewer,
} from '../../api/queries.ts';
import type { ApprovalGate, ApprovalRequest, ReviewerRequirement } from '../../api/types.ts';
import { formatDateTime } from '../../components/format.ts';
import { roleLabel } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Dialog, DialogBody, DialogFooter } from '../../components/ui/dialog.tsx';
import { Alert, ErrorState, Loading, PageError } from '../../components/ui/feedback.tsx';
import { Select, TextField } from '../../components/ui/field.tsx';
import { Card, CardHeader, Mono, PageHeader } from '../../components/ui/layout.tsx';
import { DocumentStatusBadge } from '../documents/vocabulary.tsx';
import { ReviewerDecisionPanel } from './decide.tsx';
import { ReviewerSets, ReviewerTally } from './reviewers.tsx';
import { ApprovalStatusBadge, approvalStatusMeaning, blockHeadline } from './vocabulary.tsx';

/**
 * The gate, labelled with which request it is about.
 *
 * Rendered as a heading plus the server's own sentence, never a sentence of this
 * screen's own — `explanation` is what publish answers its 409 with, and two
 * wordings for one refusal is how a dialog stops being believed.
 */
function GateCard({ gate, request }: { gate: ApprovalGate; request: ApprovalRequest }) {
  const aboutThisRequest = gate.requestId === request.id;

  return (
    <Card>
      <div className="flex flex-col gap-2">
        <h2 className="text-md font-semibold text-primary">
          {aboutThisRequest ? blockHeadline(gate.blockedBy) : 'This request has been superseded'}
        </h2>
        {aboutThisRequest ? (
          <p className={gate.mayPublish ? 'text-sm text-secondary' : 'text-sm text-primary'}>
            {gate.explanation}
          </p>
        ) : (
          <>
            <p className="text-sm text-secondary">
              A newer approval request exists on this document, and it is the one the publish gate
              answers for. Everything below is the record of what was decided here, which §7.6 keeps
              readable after the next revision replaces it.
            </p>
            <p className="text-sm text-tertiary">
              The document now:{' '}
              <span className="text-primary">{blockHeadline(gate.blockedBy)}</span> —{' '}
              {gate.explanation}
            </p>
          </>
        )}
      </div>
    </Card>
  );
}

/**
 * Adding a reviewer to a request already in flight.
 *
 * `requirement` has no default here, and the API's own schema makes it
 * mandatory on this route while leaving it optional when a request is opened.
 * That is not an inconsistency: adding somebody mid-review is a deliberate act
 * with two very different meanings, and a default would silently pick one.
 */
function AddReviewer({ requestId }: { requestId: string }) {
  const picker = usePeoplePicker();
  const add = useAddReviewer(requestId);
  const [personId, setPersonId] = useState('');
  const [requirement, setRequirement] = useState<ReviewerRequirement | ''>('');

  const options = (picker.data?.people ?? []).map((person) => ({
    value: person.id,
    label: person.name,
    description: `${person.email} · ${roleLabel(person.role)}`,
  }));

  return (
    <div className="flex flex-col gap-3 border-t border-secondary px-5 py-4">
      <div className="grid gap-3 sm:grid-cols-[2fr_1fr_auto] sm:items-end">
        <Select
          label="Add a reviewer"
          options={options}
          selectedKey={personId === '' ? null : personId}
          onSelectionChange={setPersonId}
          placeholder={picker.isPending ? 'Loading…' : 'Choose a person'}
        />
        <Select
          label="Requirement"
          options={[
            {
              value: 'advisory' as const,
              label: 'Advisory',
              description: 'Never blocks publishing.',
            },
            {
              value: 'required' as const,
              label: 'Required',
              description: 'Publishing waits for them.',
            },
          ]}
          selectedKey={requirement === '' ? null : requirement}
          onSelectionChange={setRequirement}
          placeholder="Choose one"
          isRequired
        />
        <Button
          variant="secondary"
          isPending={add.isPending}
          isDisabled={personId === '' || requirement === ''}
          onPress={() => {
            if (requirement === '') return;
            add.mutate(
              { personId, requirement },
              {
                onSuccess: () => {
                  setPersonId('');
                  setRequirement('');
                },
              },
            );
          }}
        >
          Add
        </Button>
      </div>
      <p className="text-xs text-tertiary">
        Adding only ever tightens the gate, which is why it is an edit rather than an approval
        right. Changing somebody&apos;s requirement is a removal and a re-add on purpose — an
        in-place change would let an author demote the reviewer they expect to hear &quot;no&quot;
        from.
      </p>
      {add.isError ? <ErrorState error={add.error} /> : null}
    </div>
  );
}

/**
 * Withdrawing the question — §7.6's `expired`.
 *
 * Not an override of anybody's decision, which is why the reason is optional
 * here and mandatory on §6.4's force-publish. It exists because one open request
 * per document plus a required reviewer who has left is a document that can
 * never be published again: the gate would refuse every future revision with a
 * reason nobody can act on.
 */
function ExpireRequest({ request }: { request: ApprovalRequest }) {
  const expire = useExpireApprovalRequest(request.id);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');

  return (
    <>
      <Button variant="tertiary" className="text-error-primary" onPress={() => setOpen(true)}>
        Withdraw this request
      </Button>
      <Dialog
        title="Withdraw this approval request"
        description="§7.6's expired — the question is taken back, not answered."
        isOpen={open}
        onOpenChange={setOpen}
      >
        {(close) => (
          <>
            <DialogBody>
              <p className="text-sm text-secondary">
                Nothing is approved and nothing is rejected. Decisions already recorded stay
                recorded — this closes the request so a new one can be opened, which is the only way
                past a required reviewer who has left or gone quiet.
              </p>
              <p className="text-sm text-secondary">
                A draft that moved to <span className="font-mono">in_review</span> when this opened
                goes back to <span className="font-mono">draft</span>. A document that was already
                published is untouched.
              </p>
              <TextField
                label="Why, if it is worth saying"
                value={reason}
                onChange={setReason}
                multiline
                rows={2}
                description="Optional. §7.6 does not require one — this is the withdrawal of a question, not an override of an answer."
              />
              {expire.isError ? <ErrorState error={expire.error} /> : null}
            </DialogBody>
            <DialogFooter>
              <Button variant="tertiary" onPress={close}>
                Keep it open
              </Button>
              <Button
                variant="destructive"
                isPending={expire.isPending}
                onPress={() =>
                  expire.mutate(reason.trim() ? { reason: reason.trim() } : {}, {
                    onSuccess: close,
                  })
                }
              >
                Withdraw
              </Button>
            </DialogFooter>
          </>
        )}
      </Dialog>
    </>
  );
}

export function ApprovalRequestPage({ requestId }: { requestId: string }) {
  const me = useMe();
  const query = useApprovalRequest(requestId);
  const nodeId = query.data?.request.subject.id ?? '';
  const document = useDocument(nodeId);
  const approvals = useDocumentApprovals(nodeId);
  const remove = useRemoveReviewer(requestId);

  if (query.isPending) return <Loading label="Reading the approval request…" />;
  if (query.isError) {
    return (
      <PageError
        error={query.error}
        title="Approval request"
        back={{ href: '/approvals', label: 'Review waiting on you' }}
      />
    );
  }

  const { request } = query.data;
  const viewerId = me.data?.user.id;
  // The opener is usually the owner and therefore always a reviewer, so their
  // name is already on the page. Falling back to the id beats a second request
  // to a route most reviewers cannot call — a 403 fetched to render a byline is
  // a denial in §8.5's log that nobody performed.
  const opener = request.reviewers.find((reviewer) => reviewer.personId === request.createdBy);

  return (
    <>
      <PageHeader
        title={document.data?.document.title ?? 'Approval request'}
        description={
          <>
            Editorial approval, §7.6. Publishing unblocks when every required reviewer approves;
            advisory reviewers may stay outstanding forever without holding anything up.
          </>
        }
        actions={
          nodeId ? (
            <Link
              to="/documents/$nodeId"
              params={{ nodeId }}
              className="inline-flex h-10 items-center rounded-lg border border-primary bg-primary px-3.5 text-sm font-semibold text-secondary no-underline shadow-xs hover:bg-primary-hover"
            >
              Open the document
            </Link>
          ) : null
        }
      />

      <div className="flex flex-wrap items-center gap-2 text-sm text-tertiary">
        <ApprovalStatusBadge status={request.status} />
        {document.data ? <DocumentStatusBadge status={document.data.document.status} /> : null}
        <span>
          Opened by {opener?.name ?? <Mono>{request.createdBy}</Mono>} on{' '}
          {formatDateTime(request.createdAt)}
        </span>
        {request.resolvedAt ? <span>· Resolved {formatDateTime(request.resolvedAt)}</span> : null}
        <span className="text-xs">· {approvalStatusMeaning(request.status)}</span>
      </div>

      <ReviewerDecisionPanel request={request} viewerId={viewerId} />

      {approvals.isError ? <ErrorState error={approvals.error} /> : null}
      {approvals.data ? <GateCard gate={approvals.data.gate} request={request} /> : null}

      <Card padded={false}>
        <CardHeader
          title="Reviewers"
          description={<ReviewerTally reviewers={request.reviewers} />}
          actions={request.open ? <ExpireRequest request={request} /> : null}
        />
        <div className="px-5 py-4">
          <ReviewerSets
            reviewers={request.reviewers}
            viewerId={viewerId}
            {...(request.open ? { onRemove: (personId) => remove.mutate(personId) } : {})}
            removingId={remove.isPending ? (remove.variables ?? null) : null}
          />
          {remove.isError ? (
            <div className="mt-4">
              <ErrorState error={remove.error} />
            </div>
          ) : null}
        </div>
        {request.open ? <AddReviewer requestId={request.id} /> : null}
      </Card>

      {!request.open ? (
        <Alert tone="info" title="This request is history">
          Nothing edits a closed request — not a late advisory approval, not a correction. §7.6
          keeps it readable instead: the record of who decided what and when survives the revision
          that supersedes it, which is the only thing that makes it worth anything.
        </Alert>
      ) : null}
    </>
  );
}
